import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, CheckCircle, Clock } from 'lucide-react';
import axios from 'axios';
import { useParams } from 'react-router-dom';

const API = '/api';

const InterviewSelect = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/interview-selection/${token}`);
        setData(response.data);
        
        if (response.data.already_selected) {
          setConfirmed(true);
          setSelectedSlot(response.data.selected_slot);
        }
      } catch (err) {
        console.error('Error fetching interview options:', err);
        setError('This link is invalid or has expired. Please contact High Quality Studios for assistance.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token]);

  const handleSubmit = async () => {
    if (!selectedSlot) return;

    try {
      setSubmitting(true);
      await axios.post(`${API}/interview-selection/${token}`, {
        selected_slot: selectedSlot
      });
      setConfirmed(true);
    } catch (err) {
      console.error('Error confirming selection:', err);
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Failed to confirm your selection. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading interview options...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Link Error</h2>
          <p className="text-gray-400">{error}</p>
          <a 
            href="https://hqstu.com" 
            className="inline-block mt-6 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-all"
          >
            Go to Website
          </a>
        </div>
      </div>
    );
  }

  if (confirmed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-green-900/30 to-green-800/20 border border-green-500/30 rounded-2xl p-8 max-w-md text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Interview Confirmed!</h2>
          <p className="text-gray-400 mb-6">
            Your interview has been scheduled. We&apos;ve sent you a confirmation email.
          </p>
          
          <div className="bg-black/30 rounded-xl p-6 mb-6">
            <div className="flex items-center justify-center gap-2 text-green-400 mb-4">
              <Calendar className="w-5 h-5" />
              <span className="font-semibold">Your Interview</span>
            </div>
            <p className="text-xl text-white font-bold">{selectedSlot}</p>
          </div>
          
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
            <div className="flex items-center justify-center gap-2 text-purple-400 mb-2">
              <MapPin className="w-4 h-4" />
              <span className="font-semibold text-sm">Location</span>
            </div>
            <p className="text-gray-300 text-sm">
              High Quality Studios<br />
              270 Meserole Street<br />
              Brooklyn, NY 11206
            </p>
          </div>
          
          <p className="text-sm text-gray-500 mt-6">
            Please arrive 5-10 minutes early
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900 py-12 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-500/30">
            <Calendar className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Interview Invitation</h1>
          <p className="text-gray-400">
            Hello <span className="text-purple-400 font-semibold">{data?.name}</span>!<br />
            Please select your preferred interview time.
          </p>
        </div>

        {/* Time Slots */}
        <div className="space-y-3 mb-8">
          {data?.slots?.map((slot, index) => (
            <button
              key={index}
              onClick={() => setSelectedSlot(slot)}
              className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                selectedSlot === slot
                  ? 'bg-purple-600/20 border-purple-500 shadow-lg shadow-purple-500/20'
                  : 'bg-gray-800/50 border-gray-700 hover:border-purple-500/50'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  selectedSlot === slot 
                    ? 'border-purple-500 bg-purple-500' 
                    : 'border-gray-500'
                }`}>
                  {selectedSlot === slot && (
                    <CheckCircle className="w-4 h-4 text-white" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-purple-400" />
                    <span className="text-white font-semibold">{slot}</span>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Location Info */}
        <div className="bg-gradient-to-br from-purple-900/30 to-indigo-900/20 border border-purple-500/20 rounded-xl p-6 mb-8">
          <div className="flex items-center gap-2 text-purple-400 mb-3">
            <MapPin className="w-5 h-5" />
            <span className="font-semibold">Interview Location (In-Person)</span>
          </div>
          <p className="text-gray-300">
            High Quality Studios<br />
            270 Meserole Street<br />
            Brooklyn, NY 11206
          </p>
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={!selectedSlot || submitting}
          className="w-full py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl font-bold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-purple-500/30"
        >
          {submitting ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Confirming...
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5" />
              Confirm Interview Time
            </>
          )}
        </button>

        <p className="text-center text-gray-500 text-sm mt-4">
          Can&apos;t make any of these times? Reply to the email to request different options.
        </p>
      </div>
    </div>
  );
};

export default InterviewSelect;
